import { DotLegalDatePicker, DotLegalDialog, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import DotLegalSelectApi from "@common/components/dotLegalSelectApi/DotLegalSelectApi";
import MentionInput from "@common/components/mentionInput/MentionInput";
import AuditTemplateLinkWrapper from "@common/AuditTemplateLinkWrapper";
import { arrow, leftWhiteArrowButton } from "@common/icons";
import { EntityType } from "@common/Common.types";
import { useTranslation } from "@localization/useTranslation";
import { apiUrls } from "@common/api/apiUrls";
import { QuestionnaireSelectableItem } from "../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import GroupEntityStep from "../../legalEntity/legalEntityAuditTab/groupEntityStep/GroupEntityStep";
import useCreateInternalAssessmentGroupDialog from "./CreateInternalAssessmentGroupDialog.hooks";
import { InternalAssessmentGroupSaveModel } from "../InternalAssessmentGroups.types";
import DialogHeaderWithInformation from "@common/components/dialogHeaderWithInformation/DialogHeaderWithInformation";

export interface ICreateInternalAssessmentGroupDialog {
    internalAssessmentGroupId?: string;
    internalAssessmentGroup?: InternalAssessmentGroupSaveModel;
    internalAssessmentGroupInProgress?: boolean;
    onDialogClose: () => void;
    onInternalAssessmentGroupUpdated?: () => void;
}

function CreateInternalAssessmentGroupDialog(props: ICreateInternalAssessmentGroupDialog) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const {
        model,
        step,
        isEdit,
        validation,
        selectableResponsiblesData,
        selectableResponsiblesLoading,
        selectableGroupEntitiesData,
        showSelectGroupEntityStep,
        inProgress,
        onNameChange,
        onDescriptionChange,
        onResponsibleChange,
        onDeadlineChange,
        onQuestionnaireChange,
        onGroupEntityChanged,
        onNextOrSaveClick,
        onPreviousClick,
    } = useCreateInternalAssessmentGroupDialog(props);

    const formStep = (
        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
            <Box sx={{ width: 450 }}>
                <DotLegalTextField
                    label={translateString("name")}
                    value={model.name}
                    debounce={false}
                    onChange={onNameChange}
                    toolTipText={translateString("internalAssessmentGroupNameTooltip")}
                    errorText={validation.getErrorsForDisplay("name")}
                />
                <MentionInput
                    label={translateString("description")}
                    value={model.description ?? ""}
                    debounce={false}
                    multiline
                    rows={3}
                    onChange={onDescriptionChange}
                    toolTipText={translateString("internalAssessmentGroupDescriptionTooltip")}
                    mentionSource={{ entityFromId: "", entityFromType: EntityType.InternalAssessmentGroup }}
                    manageRelationsExternal
                />
                {!props.internalAssessmentGroupInProgress && (
                    <AuditTemplateLinkWrapper>
                        <DotLegalSelectApi<QuestionnaireSelectableItem>
                            url={apiUrls().questionnaires.internalAssessment}
                            label={translateString("assessmentTemplate")}
                            placeholder={translateString("assessmentTemplate")}
                            selectedItem={model.questionnaireId}
                            onChange={(item) => onQuestionnaireChange(item?.id ?? null, item?.name)}
                            noOptionsLabel={translateString("noOptions")}
                            toolTipText={translateString("selectTemplatetInfo")}
                            errorText={validation.getErrorsForDisplay("questionnaireId")}
                        />
                    </AuditTemplateLinkWrapper>
                )}
                <DotLegalSelect
                    selectedItem={model.responsibleId}
                    options={selectableResponsiblesData}
                    isLoading={selectableResponsiblesLoading}
                    onChange={onResponsibleChange}
                    label={translateString("responsible")}
                    placeholder={translateString("responsible")}
                    noOptionsLabel={translateString("noOptions")}
                    toolTipText={translateString("assessmentResponsbileInfo")}
                    errorText={validation.getErrorsForDisplay("responsibleId")}
                />
                <DotLegalDatePicker
                    label={translateString("deadline")}
                    value={model.deadline ?? null}
                    onChange={onDeadlineChange}
                    language={getDateTimePickerLanguage()}
                    toolTipText={translateString("assessmentDeadlineInfo")}
                    error={!!validation.getErrorsForDisplay("deadline")}
                    helperMessage={validation.getErrorsForDisplay("deadline")}
                />
            </Box>
        </Box>
    );

    // The arrow and the right-aligned action row are the stepper affordance, so they belong to the button that
    // moves on to the next step - not to a plain save in a dialog that only ever has the one step.
    const isOnNextStepButton = step === "formData" && showSelectGroupEntityStep;

    const groupEntityStep = (
        <GroupEntityStep
            showGdpoIfAvailable
            onGroupEntityChanged={onGroupEntityChanged}
            groupEntityId={model.groupEntityId}
            errormsg={validation.getErrorsForDisplay("groupEntityId")}
            selectableGroupEntitiesData={selectableGroupEntitiesData}
            header={translateString("internalAssessmentGroupEntityHeader")}
            infoTranslationKey={"internalAssessmentGroupEntity"}
            checkboxTranslationKey={"internalAssessmentEntireGroup"}
            checkboxTooltip={translateString("internalAssessmentEntireGroupInfo")}
        />
    );

    return (
        <DotLegalDialog
            size="md"
            open
            inProgress={inProgress}
            header={
                <DialogHeaderWithInformation
                    header={isEdit ? translateString("editInternalAssessmentGroup") : translateString("createInternalAssessmentGroup")}
                    information={isEdit ? undefined : translateString("internalAssessmentGroupsTooltip")}
                />
            }
            buttonOkText={isOnNextStepButton ? translateString("next") : translateString("save")}
            buttonOkIcon={isOnNextStepButton ? arrow : undefined}
            previousButtonIcon={leftWhiteArrowButton}
            previousButtonText={translateString("previous")}
            buttonRight={showSelectGroupEntityStep}
            onDialogClose={props.onDialogClose}
            onOkClick={onNextOrSaveClick}
            previousButtonClick={onPreviousClick}
            disableBackdropOnCloseIfValueChange={model}
        >
            {step === "formData" ? formStep : groupEntityStep}
        </DotLegalDialog>
    );
}

export default CreateInternalAssessmentGroupDialog;
